// src/oura/components/IssuePagesRemediation.tsx
//
// The affected pages of one audit issue, each able to produce its own fix
// draft, with the draft rendered under the page it belongs to.
//
// THE RULE THAT SHAPES THIS FILE: generating is cheap and private, publishing
// writes to the client's live website. So the two get different affordances:
//
//   - GENERATE is bulk-able. A draft costs a model call and sits in our
//     database until someone reads it; a bad one is discarded at no cost to
//     anyone. Selecting twelve pages and drafting them is a reasonable thing
//     to want, so selection exists and the footer acts on it.
//
//   - PUBLISH is not bulk-able, deliberately. Every publish is a separate write
//     to someone's live site, and the bottleneck is READING the draft, not
//     clicking the button. A "publish selected" control optimises the click
//     while quietly encouraging the thing it should prevent — approving twelve
//     pieces of copy nobody read. It stays per draft, two-step, where the
//     content is on screen.
//
// That is also why there is no sticky publish CTA at the foot of the drawer: a
// primary action pinned to the viewport says "this is the thing you came to do",
// and the thing you came to do is review.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Loader2Icon,
    SparklesIcon,
    FileTextIcon,
    ImageIcon,
    LinkIcon,
    AlertTriangleIcon,
} from "lucide-react";
import { Button } from "../primitives/Button";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@component/select";
import { DraftStack, typesForIssue } from "./RemediationSection";
import { shortDate } from "../lib/oura";
import { PageIdentity } from "./PageIdentity";
import { cn } from "../lib/cn";
import BulkActionBar from "@component/bulk-action-bar";
import { useSnackbar } from "@hook/use-snackbar";
import { useWritesPaused } from "@context/maintenance";
import { createDraft, listDrafts, setPageCheckStatus, type AeoDraft } from "@service/aeo";
import type { AuditIssue, IssuePage } from "../audit";

/**
 * How many pages one bulk generate will accept.
 *
 * Until the backend batch endpoint lands (ourteam-backend#3917) this fans out
 * one request per page from the browser, so there is no server-side throttle
 * and no shared spend cap. A hard ceiling keeps a stray "select all" on a
 * 200-page issue from turning into 200 model calls.
 */
const MAX_BULK = 25;

/**
 * What a person can say about one page and one finding (ourteam-backend#4530).
 *
 * Three values, not four: there is no "won't fix" here because that decision is
 * about the PAGE, not about this finding on it, and it already has a home — the
 * reviewer sets a page's role on the Pages tab and it moves to Out of scope.
 * A second way to say the same thing in a different place is how two surfaces
 * come to disagree about which pages are being worked on.
 */
type OperatorStatus = "not_started" | "in_progress" | "done";

const STATUS_LABEL: Record<OperatorStatus, string> = {
    not_started: "Not started",
    in_progress: "In progress",
    done: "Done",
};

const STATUS_CHIP: Record<OperatorStatus, string> = {
    not_started: "border-gray-200 bg-gray-100 text-gray-600",
    in_progress: "border-blue-200 bg-blue-50 text-blue-700",
    done: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const STATUS_ORDER: OperatorStatus[] = ["not_started", "in_progress", "done"];

/** A column head, in sentence case like every other label in Oura. */
const HEAD = "text-xs font-medium text-gray-500";
/** The Page column takes the slack, down to a floor that keeps a title
 *  readable; below it the table scrolls sideways instead. */
const PAGE_COL = "min-w-[200px] flex-1";

export function IssuePagesRemediation({
    issue,
    businessId,
    onRefreshData,
    publishBlockedReason,
    publishUnsupported,
}: {
    issue: AuditIssue;
    /** Live business id. Absent on a fixture, where drafts are not a real thing. */
    businessId?: string;
    /** #3514: re-read the assessment after a status write. `localStatus` below is
     *  only an optimistic layer and dies with the component, so without this a
     *  status set here reverts the moment the card is closed and reopened. */
    onRefreshData?: () => void | Promise<void>;
    /** See DraftRow — why publishing cannot work right now (#3400). */
    publishBlockedReason?: string;
    /** See DraftRow — threaded through unchanged (#3692). */
    publishUnsupported?: (draftType: string) => string | undefined;
}) {
    const snackbar = useSnackbar();
    const writesPaused = useWritesPaused();
    /**
     * What a draft can fix HERE, as the check itself declares it (#4529 §1).
     *
     * This used to be a hardcoded frontend map, and it had drifted: Core Web
     * Vitals offered to write an FAQ block, and "Links to pages that no longer
     * exist" offered a meta description. Neither does anything about the
     * finding. An EMPTY list is now a real answer and the generator disappears.
     */
    const options = useMemo(
        () => typesForIssue(issue.key, issue.draftTypes),
        [issue.key, issue.draftTypes],
    );
    /**
     * Whether Oura can write a fix for this finding at all.
     *
     * `options.length` covers the checks no draft resolves (#4529 §1). `locates`
     * covers the ones whose answer is WHERE the problem is, not what to write —
     * missing alt text and dead outbound links. For those the per-page
     * `locations` name the fix and a "Draft fix" button would only offer a
     * generation nobody wants, so it is hidden too.
     */
    const canDraft = options.length > 0 && !issue.locates;
    // One draft type per finding now (#4529 §1), so there is nothing to choose
    // between — the type is fixed and the "What should we write?" dropdown is
    // gone. Kept as state only because generateOne sends it.
    const [type] = useState(options[0]?.key ?? "");
    const [drafts, setDrafts] = useState<AeoDraft[] | null>(null);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    /**
     * Statuses written in this session, over the top of what the payload says.
     *
     * The assessment is fetched once by the screen above and a status change
     * does not re-fetch it, so without this a page marked done reverts on the
     * next render — the operator's own click undone in front of them. The
     * server holds the durable copy; this is only the gap until the next load.
     */
    const [localStatus, setLocalStatus] = useState<Record<string, OperatorStatus>>({});
    const [marking, setMarking] = useState(false);

    const statusOf = useCallback(
        (pg: IssuePage): OperatorStatus | null =>
            localStatus[pg.url] ?? (pg.operatorStatus as OperatorStatus | null) ?? null,
        [localStatus],
    );

    /**
     * Every page in this finding is in scope.
     *
     * A page set aside on the Pages tab (product, utility or placeholder) is
     * dropped from the finding upstream — `setAsideOf` in SiteAudit removes it
     * before the drawer ever sees it — because an out-of-scope page does not
     * need work here and listing it, even under a separate heading, puts it
     * back on the worklist the reviewer just cleared. The header still says
     * "N out of scope" so the count stays honest without the rows.
     */
    const shownPages = issue.pages;
    const workPages = issue.pages;

    /**
     * Whether the issue-specific column earns its width.
     *
     * It carries the finding's own value per page — a 4xx status code, a
     * redirect hop count, a word count — which is worth a column when it varies
     * from row to row. But for "missing structured data" every row just reads
     * "No structured data", and for a missing meta description there is no
     * before to show at all, so the column becomes the issue title repeated
     * down the page: it answers neither "why is it missing" nor "where", and it
     * costs the Page column the room those long titles actually need. Shown only
     * when at least one value is present AND they are not all identical.
     */
    const showMeta = useMemo(() => {
        const vals = shownPages.map((p) => (p.meta ?? "").trim());
        if (!vals.some(Boolean)) return false;
        return new Set(vals).size > 1;
    }, [shownPages]);
    const [working, setWorking] = useState<Set<string>>(new Set());
    const [bulk, setBulk] = useState<{ done: number; total: number } | null>(null);


    /**
     * Whether this issue can genuinely be fixed more than one way.
     *
     * Most cannot. A missing meta description is fixed by writing a meta
     * description, full stop — offering a one-option "What should we write?"
     * dropdown asked a question with no answer to give, and read as though a
     * choice were being withheld. Only a finding with a real fork gets a control.
     */
    const multipleTypes = options.length > 1;

    const load = useCallback(async () => {
        if (!businessId) return;
        setDrafts(await listDrafts(businessId));
    }, [businessId]);

    useEffect(() => {
        let cancelled = false;
        setDrafts(null);
        if (!businessId) return;
        void listDrafts(businessId).then((d) => {
            if (!cancelled) setDrafts(d);
        });
        return () => { cancelled = true; };
    }, [businessId]);

    /**
     * Drafts that belong to each affected page, so they render under it.
     *
     * Scoped to THIS issue first. `listDrafts(businessId)` returns every draft
     * the business has, and one page routinely carries drafts for several
     * different issues — a thin `/services` page can be short on copy AND
     * missing a meta description. Grouping by url alone therefore surfaced
     * another issue's draft inside this drawer, where the only thing naming
     * which issue it addresses is the drawer header itself.
     *
     * That is a wrong-publish path onto a client's live site: DraftStack's
     * publish action would push issue A's copy from issue B's context. It also
     * mislabelled the button — a page with any unrelated draft read "Draft
     * again" when this issue had never been drafted at all.
     */
    const draftsByPage = useMemo(() => {
        const map = new Map<string, AeoDraft[]>();
        for (const d of drafts ?? []) {
            if (d.issueKey !== issue.key) continue;
            const list = map.get(d.pageUrl);
            if (list) list.push(d);
            else map.set(d.pageUrl, [d]);
        }
        return map;
    }, [drafts, issue.key]);

    const generateOne = async (url: string) => {
        if (!businessId) return false;
        setWorking((w) => new Set(w).add(url));
        const draft = await createDraft(businessId, {
            page_url: url,
            issue_key: issue.key,
            draft_type: type,
        });
        setWorking((w) => {
            const next = new Set(w);
            next.delete(url);
            return next;
        });
        return !!draft;
    };

    const generateSelected = async () => {
        const urls = workPages.map((p) => p.url).filter((u) => selected.has(u));
        if (urls.length === 0) return;
        setBulk({ done: 0, total: urls.length });
        const failed: string[] = [];
        // Sequential on purpose: one page at a time is slower but it cannot
        // stampede the provider, and a failure stops at a known point instead of
        // leaving an unknown subset half-written. Swap this loop for the batch
        // endpoint (ourteam-backend#3917) when it exists.
        for (let i = 0; i < urls.length; i++) {
            const ok = await generateOne(urls[i]);
            if (!ok) failed.push(urls[i]);
            setBulk({ done: i + 1, total: urls.length });
        }
        setBulk(null);
        setSelected(new Set());
        await load();
        if (failed.length === 0) {
            snackbar.showSuccess(
                `${urls.length} ${urls.length === 1 ? "draft" : "drafts"} ready`,
                "Review each one before publishing.",
            );
        } else {
            snackbar.showError(
                `${failed.length} of ${urls.length} could not be drafted`,
                failed.slice(0, 3).join(", ") + (failed.length > 3 ? " and others" : ""),
            );
        }
    };

    /**
     * Record what the operator says about the selected pages (#4530 §3).
     *
     * One request for the whole selection, not a loop: the ask was to change
     * many at once, and fanning out per row from the browser is the same
     * feature with the complaint left in.
     *
     * Uncapped, unlike drafting. A status write is a row in our own database;
     * MAX_BULK exists because a draft is a model call against someone's spend.
     */
    const markSelected = async (next: OperatorStatus) => {
        if (!businessId) return;
        const urls = workPages.map((p) => p.url).filter((u) => selected.has(u));
        if (urls.length === 0) return;
        setMarking(true);
        const ok = await setPageCheckStatus(businessId, issue.key, urls, next);
        setMarking(false);
        if (!ok) {
            snackbar.showError(
                "Could not save the status",
                "Nothing was changed. Please try again.",
            );
            return;
        }
        setLocalStatus((cur) => {
            const map = { ...cur };
            for (const u of urls) map[u] = next;
            return map;
        });
        setSelected(new Set());
        // The durable copy lives on the server; localStatus above only carries it
        // until this component unmounts. Re-read so the status survives closing
        // the card, reopening it, and a full reload (#3514). Deliberately not
        // awaited: the rows already show the new status optimistically, so
        // blocking on the round trip would only add a visible delay.
        void onRefreshData?.();
        snackbar.showSuccess(
            `${urls.length} ${urls.length === 1 ? "page" : "pages"} marked ${STATUS_LABEL[next].toLowerCase()}`,
            next === "done"
                ? "The next crawl still checks these. If it disagrees, both answers are kept."
                : "Recorded against this finding.",
        );
    };

    /**
     * A published fix closes its own page (#3637).
     *
     * The complaint was a row still reading "Not started" under a draft marked
     * Published — the product knew the page had been fixed and made the
     * operator say it again. Only on a publication that actually LANDED: a
     * queued or failed write has changed nothing on the site, and marking those
     * done would be the same lie in the other direction.
     *
     * Never downgrades. A page already marked done by hand stays done, and one
     * an operator deliberately set back to in_progress is not overruled by a
     * later republish of the same draft.
     */
    const closePublishedPage = async (page: IssuePage) => {
        if (!businessId) return;
        if (statusOf(page) === "done") return;
        const ok = await setPageCheckStatus(businessId, issue.key, [page.url], "done");
        if (!ok) {
            // Not a failure worth a red toast: the publish itself worked, and
            // the operator can still set the status by hand. Say what is true.
            snackbar.showError(
                "Published, but the status was not saved",
                "The change is live. Mark the page done when you get a moment.",
            );
            return;
        }
        setLocalStatus((cur) => ({ ...cur, [page.url]: "done" }));
        void onRefreshData?.();
    };

    /**
     * The operator's word that they fixed a builder-owned page themselves
     * (#3639). Same write as marking it done by hand, because that is what it
     * is — the value of the button is that it sits where the refusal was
     * explained, not that it records something different.
     *
     * The crawl still checks the page. If it disagrees, `statusDisagreement`
     * brings both answers back and the row shows them, which is why this can be
     * offered at all: nobody is being asked to take the claim on trust.
     */
    const markFixedByHand = async (page: IssuePage) => {
        if (!businessId) return;
        const ok = await setPageCheckStatus(businessId, issue.key, [page.url], "done");
        if (!ok) {
            snackbar.showError(
                "Could not save the status",
                "Nothing was changed. Please try again.",
            );
            return;
        }
        setLocalStatus((cur) => ({ ...cur, [page.url]: "done" }));
        void onRefreshData?.();
        snackbar.showSuccess(
            "Marked done",
            "The next crawl still checks this page. If it disagrees, both answers are kept.",
        );
    };

    const toggle = (url: string) =>
        setSelected((cur) => {
            const next = new Set(cur);
            if (next.has(url)) next.delete(url);
            else next.add(url);
            return next;
        });

    // Only the worklist can be selected: the out-of-scope tab has no checkboxes.
    const allUrls = workPages.map((p) => p.url);
    /**
     * What select-all can actually select, which is the capped set.
     *
     * The header box used to check `allUrls.every(...)` while the click
     * selected only the first MAX_BULK. On a finding with more pages than the
     * cap those two can never agree, so the box never ticked and the second
     * click re-selected the same rows instead of clearing them.
     */
    const selectable = allUrls.slice(0, MAX_BULK);
    const allSelected = selectable.length > 0 && selectable.every((u) => selected.has(u));
    // Anything selected clears. "Select all" that cannot unselect is a trap.
    const toggleAll = () => setSelected(selected.size > 0 ? new Set() : new Set(selectable));

    const overCap = selected.size > MAX_BULK;

    return (
        <section className="mt-2">
            {shownPages.length === 0 ? (
                // The check counted pages but listed none (older scans, or a
                // payload that sends counts only). An empty table under a
                // "Showing 0 of 2" line read as a broken drawer.
                <p className="m-0 text-gray-500">
                    This scan counted {issue.count} affected {issue.count === 1 ? "page" : "pages"} but did not list which.
                </p>
            ) : (
                // Scrolls sideways on a narrow screen rather than squeezing the
                // Page column to one letter a line: `min-w-min` holds every row at
                // the Page column's floor plus the fixed columns beside it. The
                // drawer body still scrolls vertically as one thing.
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <div className="min-w-min">
                        <div className="flex items-center gap-3 border-b border-gray-200 bg-gray-50 pl-1 pr-3 py-2">
                            {/* A column of its own, not a bare input wedged between the
                                cell padding and the page title. The label makes the whole
                                column the hit target, so ticking a row does not depend on
                                finding a 16px square. */}
                            {businessId && (
                                <label className="-my-2 flex w-10 flex-shrink-0 cursor-pointer items-center justify-center self-stretch">
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        onChange={toggleAll}
                                        aria-label="Select all affected pages"
                                        className="h-4 w-4 cursor-pointer accent-blue-600"
                                    />
                                </label>
                            )}
                            {/* Widths sized to what each column actually holds. The
                                Page column takes the slack (it identifies the row);
                                the metric and the date are fixed and CENTRED so a
                                short value ("1 of 1", "11 Aug 2026") sits under its
                                header instead of hugging the far left of a wide cell. */}
                            <span className={cn(HEAD, PAGE_COL)}>Page</span>
                            {showMeta && (
                                <span className={cn(HEAD, "w-48 flex-shrink-0 text-center")}>
                                    {issue.pageColLabel}
                                </span>
                            )}
                            <span className={cn(HEAD, "w-28 flex-shrink-0 whitespace-nowrap text-center")}>
                                Discovered
                            </span>
                            {/* What WE said about this page, next to what the crawl
                                found (#4530 §3). Its own column rather than a badge on
                                the page cell: the two are different kinds of claim and
                                putting them in one place is what made a hand-fix
                                invisible until the next crawl. */}
                            {businessId && (
                                <span className={cn(HEAD, "w-32 flex-shrink-0 whitespace-nowrap text-center")}>
                                    Status
                                </span>
                            )}
                            {businessId && canDraft && <span className="w-28 flex-shrink-0" />}
                        </div>

                        <div className="divide-y divide-gray-100">
                            {shownPages.map((pg) => {
                                const pageDrafts = draftsByPage.get(pg.url) ?? [];
                                const busy = working.has(pg.url);
                                return (
                                    <div key={pg.url}>
                                        <div className="flex items-center gap-3 pl-1 pr-3 py-2.5">
                                            {businessId && (
                                                <label className="-my-2.5 flex w-10 flex-shrink-0 cursor-pointer items-center justify-center self-stretch">
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(pg.url)}
                                                        onChange={() => toggle(pg.url)}
                                                        aria-label={`Select ${pg.title}`}
                                                        className="h-4 w-4 cursor-pointer accent-blue-600"
                                                    />
                                                </label>
                                            )}
                                            {/* The SAME page presentation the Crawled Pages
                                                table uses: name on top, path beneath as the
                                                link. This block used to invert that: the path
                                                in blue on the first line with the title
                                                underneath in grey, so the two tables showing
                                                the same crawl put the same two facts in
                                                opposite places, and the thing a reader scans
                                                for was the smaller of the two. Sharing the
                                                component is also what keeps them from drifting
                                                again. */}
                                            <div className={PAGE_COL}>
                                                <PageIdentity title={pg.title} url={pg.url} />
                                            </div>
                                            {showMeta && (
                                                <span className="w-48 flex-shrink-0 text-center text-sm text-gray-600">{pg.meta}</span>
                                            )}
                                            <span className="w-28 flex-shrink-0 whitespace-nowrap text-center text-sm text-gray-500">{shortDate(pg.discovered)}</span>
                                            {businessId && (
                                                <div className="flex w-32 flex-shrink-0 flex-col items-center text-center">
                                                    <PageStatusCell status={statusOf(pg)} disagreement={pg.statusDisagreement} />
                                                </div>
                                            )}
                                            {businessId && canDraft && (
                                                <div className="w-28 flex-shrink-0 text-right">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        writes
                                                        disabled={busy || !!bulk}
                                                        onClick={() => void generateOne(pg.url).then(load)}
                                                    >
                                                        {busy ? (
                                                            <>
                                                                <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                                                Writing…
                                                            </>
                                                        ) : pageDrafts.length > 0 ? (
                                                            "Draft again"
                                                        ) : (
                                                            "Draft fix"
                                                        )}
                                                    </Button>
                                                </div>
                                            )}
                                        </div>

                                        {/* WHERE the problem is, for the findings no draft
                                            can fix (#4529 §2). Under the page, because
                                            that is the only place the list means
                                            anything: it is the answer the generic "fix
                                            the link" steps were standing in for. */}
                                        <PageLocations page={pg} />

                                        {/* The draft sits under the page it belongs to, where
                                            the URL it will change is still on screen. */}
                                        {pageDrafts.length > 0 && (
                                            <div className="space-y-2.5 px-3 pb-3 pt-1">
                                                <DraftStack
                                                    drafts={pageDrafts}
                                                    onPublished={(landed) => {
                                                        void load();
                                                        if (landed) void closePublishedPage(pg);
                                                    }}
                                                    // Only where there is a page status to
                                                    // set. A fixture has no server to tell.
                                                    onMarkedByHand={businessId ? () => markFixedByHand(pg) : undefined}
                                                    showType={multipleTypes}
                                                    publishBlockedReason={publishBlockedReason}
                                                    publishUnsupported={publishUnsupported}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* The rows actually on screen against the crawl's count: a check
                lists up to a sample of the pages it counted. */}
            {shownPages.length > 0 && issue.count > shownPages.length && (
                <p className="mt-2 text-xs text-gray-400">
                    Showing {shownPages.length} of {issue.count} affected pages.
                </p>
            )}

            {businessId && canDraft && drafts === null && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                    <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                    Loading existing drafts…
                </p>
            )}
            {businessId && canDraft && drafts?.length === 0 && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                    <FileTextIcon className="h-3.5 w-3.5" />
                    No drafts for this issue yet.
                </p>
            )}

            {/* The house bulk bar, the one Recruiter and Trainer already use,
                rather than a blue banner invented here. Anchored to the drawer
                (absolute, not fixed) so it floats over this table and not over
                the whole application. */}
            {businessId && selected.size > 0 && (
                <BulkActionBar
                    label={`${selected.size} ${selected.size === 1 ? "page" : "pages"} selected`}
                    onClear={() => setSelected(new Set())}
                    className="sticky bottom-4 left-auto right-auto mx-auto mt-3 w-fit translate-x-0"
                >
                    {/* Marking is always available, including on the findings
                        no draft can fix — those are exactly the ones somebody
                        goes and fixes by hand, and until this existed there was
                        nowhere to say they had (#4530 §3). */}
                    <Select
                        value=""
                        onValueChange={(v) => void markSelected(v as OperatorStatus)}
                        disabled={marking || !!bulk || writesPaused}
                    >
                        <SelectTrigger className="h-8 w-[150px] text-sm" aria-label="Mark the selected pages">
                            {marking ? (
                                <span className="flex items-center gap-1.5 text-gray-500">
                                    <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                    Saving…
                                </span>
                            ) : (
                                <span className="text-gray-600">Mark as…</span>
                            )}
                        </SelectTrigger>
                        <SelectContent>
                            {STATUS_ORDER.map((k) => (
                                <SelectItem key={k} value={k}>{STATUS_LABEL[k]}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {canDraft && (
                        <>
                            {overCap && (
                                <span className="text-xs text-rose-700">Draft at most {MAX_BULK} at a time.</span>
                            )}
                            <Button size="sm" writes disabled={!!bulk || overCap || marking} onClick={() => void generateSelected()}>
                                {bulk ? (
                                    <>
                                        <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
                                        Writing {bulk.done} of {bulk.total}…
                                    </>
                                ) : (
                                    <>
                                        <SparklesIcon className="h-3.5 w-3.5" />
                                        Draft {selected.size} {selected.size === 1 ? "fix" : "fixes"}
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                </BulkActionBar>
            )}

            <p className={cn("mt-2 text-xs text-gray-400")}>
                {canDraft
                    ? "Nothing reaches the live site until you open a draft and publish it."
                    : "Nothing here can be written for you. Mark a page done once you have fixed it, and the next crawl checks it."}
            </p>
        </section>
    );
}

/**
 * What we said about this page, and whether the crawl agrees.
 *
 * The disagreement is the interesting half. A page marked done that the latest
 * crawl still flags neither reverts nor stays quietly done: reverting calls the
 * operator wrong, keeping it calls the crawl wrong, and only one of those is
 * checkable afterwards. So both answers are kept and this says so, in the row,
 * where whoever marked it will see it.
 */
function PageStatusCell({
    status,
    disagreement,
}: {
    status: OperatorStatus | null;
    disagreement?: "still_failing" | null;
}) {
    // A page nobody has touched reads as "Not started", not a blank: every
    // affected page starts as work not yet begun, so that is the honest default
    // and it gives the column something to align to on every row.
    const shown: OperatorStatus = status ?? "not_started";
    return (
        <div className="flex min-w-0 flex-col items-center">
            <span
                className={cn(
                    "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
                    STATUS_CHIP[shown],
                )}
            >
                {STATUS_LABEL[shown]}
            </span>
            {disagreement === "still_failing" && (
                <span className="mt-1 flex items-start gap-1 text-center text-[11px] leading-snug text-amber-700">
                    <AlertTriangleIcon className="mt-px h-3 w-3 flex-shrink-0" />
                    The last crawl still finds this
                </span>
            )}
        </div>
    );
}

/**
 * The offending elements on one page (ourteam-backend#4529 §2, #4514).
 *
 * These checks have no draft to offer, and the three-step "fix the link" list
 * they used to carry was three restatements of itself occupying the space this
 * belongs in. A person fixing a page needs to know WHICH image has no
 * description and WHICH words carry a dead link, not to be told again that dead
 * links are bad.
 *
 * `deadLinks` is preferred over `locations` where both are present: it carries
 * the destination and why it failed, which is what decides whether the link is
 * repointed or removed.
 */
function PageLocations({ page }: { page: IssuePage }) {
    const dead = page.deadLinks ?? [];
    const spots = page.locations ?? [];
    if (dead.length === 0 && spots.length === 0) return null;

    return (
        <ul className="space-y-1 border-t border-gray-100 bg-gray-50/60 px-3 py-2.5 pl-14">
            {dead.length > 0
                ? dead.map((link, i) => (
                    <li key={`${link.url}-${i}`} className="flex items-start gap-2 text-xs leading-relaxed">
                        <LinkIcon className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
                        <span className="min-w-0">
                            {/* The words first: that is what a person searches
                                the page for. The destination second, because
                                that is what they change it to or delete. */}
                            {link.anchor && <span className="font-medium text-gray-700">“{link.anchor}” </span>}
                            <span className="break-all text-gray-500">{link.url}</span>
                            {(link.reason || link.status) && (
                                <span className="text-gray-400">
                                    {" · "}
                                    {link.status ? `${link.status}` : ""}
                                    {link.status && link.reason ? " " : ""}
                                    {link.reason ?? ""}
                                </span>
                            )}
                        </span>
                    </li>
                ))
                : spots.map((spot, i) => (
                    <li key={`${spot.src ?? spot.href ?? i}`} className="flex items-start gap-2 text-xs leading-relaxed">
                        {spot.element === "img" ? (
                            <ImageIcon className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
                        ) : (
                            <LinkIcon className="mt-0.5 h-3 w-3 flex-shrink-0 text-gray-400" />
                        )}
                        <span className="min-w-0 break-all text-gray-500">
                            {spot.text ? <span className="font-medium text-gray-700">“{spot.text}” </span> : null}
                            {spot.src ?? spot.href ?? ""}
                        </span>
                    </li>
                ))}
        </ul>
    );
}
